import { MGPOptional, MGPFallible, Set, MGPValidation } from '@everyboard/lib';
import { GameNode } from '../../jscaip/AI/GameNode';
import { GameStatus } from '../../jscaip/GameStatus';
import { Orthogonal } from '../../jscaip/Orthogonal';
import { Player, PlayerOrNone } from '../../jscaip/Player';
import { Rules } from '../../jscaip/Rules';
import { RulesFailure } from '../../jscaip/RulesFailure';
import { TableUtils } from '../../jscaip/TableUtils';
import { PylosCoord } from './PylosCoord';
import { PylosFailure } from './PylosFailure';
import { PylosMove } from './PylosMove';
import { PylosState } from './PylosState';
export class PylosNode extends GameNode {
}
export class PylosRules extends Rules {
    static singleton = MGPOptional.empty();
    static get() {
        if (PylosRules.singleton.isAbsent()) {
            PylosRules.singleton = MGPOptional.of(new PylosRules());
        }
        return PylosRules.singleton.get();
    }
    getInitialState() {
        const board0 = TableUtils.create(4, 4, PlayerOrNone.NONE);
        const board1 = TableUtils.create(3, 3, PlayerOrNone.NONE);
        const board2 = TableUtils.create(2, 2, PlayerOrNone.NONE);
        const board3 = [[PlayerOrNone.NONE]];
        const turn = 0;
        return new PylosState([board0, board1, board2, board3], turn);
    }
    static getStateInfo(state) {
        const freeToMove = [];
        const landable = [];
        for (let z = 0; z < 3; z++) {
            for (let y = 0; y < (4 - z); y++) {
                for (let x = 0; x < (4 - z); x++) {
                    const c = new PylosCoord(x, y, z);
                    if (state.getPieceAt(c) === state.getCurrentPlayer() &&
                        state.isSupporting(c) === false) {
                        freeToMove.push(c);
                    }
                    if (state.isLandable(c)) {
                        landable.push(c);
                    }
                }
            }
        }
        return { freeToMove, landable };
    }
    static getClimbingMoves(stateInfo) {
        const moves = [];
        for (const startingCoord of stateInfo.freeToMove) {
            for (const landingCoord of stateInfo.landable) {
                if (landingCoord.isHigherThan(startingCoord) &&
                    landingCoord.getLowerPieces().some((c) => startingCoord.equals(c)) === false) {
                    const newMove = PylosMove.ofClimb(startingCoord, landingCoord, []);
                    moves.push(newMove);
                }
            }
        }
        return moves;
    }
    static getDropMoves(stateInfo) {
        const drops = [];
        for (const landableCoord of stateInfo.landable) {
            const newMove = PylosMove.ofDrop(landableCoord, []);
            drops.push(newMove);
        }
        return drops;
    }
    static canCapture(state, landingCoord) {
        const currentPlayer = state.getCurrentPlayer();
        for (const vertical of [Orthogonal.UP, Orthogonal.DOWN]) {
            const firstNeighbors = landingCoord.getNextValid(vertical);
            if (firstNeighbors.isPresent() && state.getPieceAt(firstNeighbors.get()) === currentPlayer) {
                for (const horizontal of [Orthogonal.LEFT, Orthogonal.RIGHT]) {
                    const secondNeighbors = firstNeighbors.get().getNextValid(horizontal);
                    if (secondNeighbors.isPresent() &&
                        state.getPieceAt(secondNeighbors.get()) === currentPlayer) {
                        const thirdDirection = vertical.getOpposite();
                        const thirdNeighbors = secondNeighbors.get().getNextValid(thirdDirection).get();
                        if (state.getPieceAt(thirdNeighbors) === currentPlayer) {
                            return true;
                        }
                    }
                }
            }
        }
        return false;
    }
    static getPossibleCaptures(state) {
        let possibleCapturesSets = new Set();
        const freeToMoveFirsts = state.getFreeToMoves();
        for (const freeToMoveFirst of freeToMoveFirsts) {
            possibleCapturesSets = possibleCapturesSets.addElement(new Set([freeToMoveFirst]));
            const secondState = state.removePieceAt(freeToMoveFirst);
            const freeToMoveThens = secondState.getFreeToMoves();
            for (const freeToMoveThen of freeToMoveThens) {
                const captures = new Set([freeToMoveFirst, freeToMoveThen]);
                possibleCapturesSets = possibleCapturesSets.addElement(captures);
            }
        }
        return possibleCapturesSets;
    }
    static isValidCapture(state, move, capture) {
        const currentPlayer = state.getCurrentPlayer();
        if (capture.equals(move.landingCoord) === false &&
            state.getPieceAt(capture) !== currentPlayer) {
            return false;
        }
        const supportedPieces = capture.getHigherCoords()
            .filter((coord) => state.getPieceAt(coord).isPlayer() &&
            coord.equals(move.firstCapture.get()) === false);
        return supportedPieces.length === 0;
    }
    static getGameStatus(node) {
        const ownershipMap = node.gameState.getPiecesRepartition();
        if (ownershipMap.get(Player.ZERO) === 15) {
            return GameStatus.ONE_WON;
        }
        else if (ownershipMap.get(Player.ONE) === 15) {
            return GameStatus.ZERO_WON;
        }
        else {
            return GameStatus.ONGOING;
        }
    }
    applyLegalMove(move, state, _config, _info) {
        return state.applyLegalMove(move);
    }
    isLegal(move, state) {
        const startingCoordLegality = this.isLegalStartingCoord(move, state);
        if (startingCoordLegality.isFailure()) {
            return startingCoordLegality.toOtherFallible();
        }
        const stateWithLeftStartingCoord = startingCoordLegality.get();
        const landingCoordLegality = this.isLegalLandingCoord(move, stateWithLeftStartingCoord);
        if (landingCoordLegality.isFailure()) {
            return landingCoordLegality.toOtherFallible();
        }
        const stateAfterPieceLanding = landingCoordLegality.get();
        const capturesLegality = this.isLegalCaptures(move, stateAfterPieceLanding);
        if (capturesLegality.isFailure()) {
            return capturesLegality;
        }
        return MGPValidation.SUCCESS;
    }
    isLegalStartingCoord(move, initialState) {
        const opponent = initialState.getCurrentOpponent();
        if (move.startingCoord.isPresent()) {
            const startingCoord = move.startingCoord.get();
            const startingPiece = initialState.getPieceAt(startingCoord);
            if (startingPiece === opponent) {
                return MGPFallible.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_OPPONENT());
            }
            else if (startingPiece.isNone()) {
                return MGPFallible.failure(RulesFailure.MUST_CHOOSE_OWN_PIECE_NOT_EMPTY());
            }
            const supportedPieces = startingCoord.getHigherCoords()
                .filter((coord) => initialState.getPieceAt(coord).isPlayer());
            if (supportedPieces.length === 0) {
                const stateWithLeftStartingCoord = initialState.removePieceAt(move.startingCoord.get());
                return MGPFallible.success(stateWithLeftStartingCoord);
            }
            else {
                return MGPFallible.failure(PylosFailure.CANNOT_MOVE_SUPPORTING_PIECE());
            }
        }
        return MGPFallible.success(initialState);
    }
    isLegalLandingCoord(move, stateAfterClimbStart) {
        if (stateAfterClimbStart.getPieceAt(move.landingCoord).isPlayer()) {
            return MGPFallible.failure(RulesFailure.MUST_LAND_ON_EMPTY_SPACE());
        }
        if (stateAfterClimbStart.isLandable(move.landingCoord)) {
            return MGPFallible.success(stateAfterClimbStart.dropCurrentPlayersPieceAt(move.landingCoord));
        }
        else {
            return MGPFallible.failure(PylosFailure.SHOULD_HAVE_SUPPORTING_PIECES());
        }
    }
    isLegalCaptures(move, postMoveState) {
        if (move.firstCapture.isAbsent()) {
            return MGPValidation.SUCCESS;
        }
        if (PylosRules.canCapture(postMoveState, move.landingCoord) === false) {
            return MGPValidation.failure(PylosFailure.CANNOT_CAPTURE());
        }
        if (PylosRules.isValidCapture(postMoveState, move, move.firstCapture.get())) {
            const afterFirstCapture = postMoveState.removePieceAt(move.firstCapture.get());
            if (move.secondCapture.isAbsent()) {
                return MGPValidation.SUCCESS;
            }
            if (PylosRules.isValidCapture(afterFirstCapture, move, move.secondCapture.get())) {
                return MGPValidation.SUCCESS;
            }
            else {
                return MGPValidation.failure(PylosFailure.INVALID_SECOND_CAPTURE());
            }
        }
        else {
            return MGPValidation.failure(PylosFailure.INVALID_FIRST_CAPTURE());
        }
    }
    getGameStatus(node) {
        return PylosRules.getGameStatus(node);
    }
}
